/* =======================================================================
   Knowledge Base — the follow-along drawer. Azure item 416, phase 2.

   Leave the Knowledge Base with an article open and it compresses into a
   resizable panel on the right, so the steps stay on screen while the
   work happens in the actual tab. Mounted once in LayoutWithTopBar, as a
   sibling of every page's outlet, so it survives navigation — which is
   also what keeps its scroll position: nothing re-renders the body when
   the reader changes tab, so there is nothing to restore.

   Three states, all in sessionStorage (makaniKbDrawer):
     open        the panel, with the article and its own search
     dismissed   collapsed to the "Help" handle on the screen's edge
     hidden      the handle dismissed too — back only via the Knowledge Base

   It shows nothing at all on the Knowledge Base page itself, and nothing
   when no article has been opened this session.
   ======================================================================= */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { byId, strip } from './articles';
import { matches, searchTerms, termRe } from './search';
import { renderArticle, highlight } from './render';
import { KB_BASE, KB_CHANGE, SESSION, readJson, writeJson, lastArticleSlug, setLastArticle, announce, requestWalkthrough, isWalkthroughRunning } from './kbSession';
import './kb.css';

const DW_MIN = 300;
const dwMax = () => Math.max(DW_MIN, Math.min(760, window.innerWidth - 257 - 520));

function snippetFor(a, stems){
  const full = strip(a.answer) + ' ' + strip(a.body);
  const m = full.match(termRe(stems, 'i'));
  const at = m ? full.indexOf(m[0]) : -1;
  if (at < 0) return strip(a.answer).slice(0, 180) + '…';
  const start = Math.max(0, at - 75);
  return (start ? '…' : '') + full.slice(start, at + 145) + '…';
}

export default function KbDrawer(){
  const location = useLocation();
  const navigate = useNavigate();

  /* ---- what to show ---- */
  const [slug, setSlug] = useState(lastArticleSlug);
  useEffect(() => {
    const sync = () => { setSlug(lastArticleSlug()); setSt(readJson('makaniKbDrawer', { dismissed: false, hidden: false, width: 400 })); };
    window.addEventListener(KB_CHANGE, sync);
    return () => window.removeEventListener(KB_CHANGE, sync);
  }, []);
  /* the page writes makaniKbLast as it navigates; re-read on every route change too */
  useEffect(() => { setSlug(lastArticleSlug()); }, [location.pathname]);

  const article = useMemo(() => { const a = slug ? byId(slug) : null; return a && !a.stub ? a : null; }, [slug]);
  const onKbPage = location.pathname.startsWith(KB_BASE);
  /* set by the walkthrough as it starts and ends, and announced, so the sync
     above re-renders this on both edges */
  const walking = isWalkthroughRunning();

  /* ---- drawer state ---- */
  const [st, setSt] = useState(() => readJson('makaniKbDrawer', { dismissed: false, hidden: false, width: 400 }));
  /* Storage is the source of truth — the page and the walkthrough write it too.
     So a change is written there first and then mirrored into state, never the
     other way round: a write-back effect on state raced the walkthrough's
     restore and left the drawer stuck dismissed. */
  const patch = useCallback(p => {
    const next = { ...readJson('makaniKbDrawer', { dismissed: false, hidden: false, width: 400 }), ...p };
    writeJson('makaniKbDrawer', next);
    setSt(next);
  }, []);


  const [query, setQuery] = useState('');
  const flagsHidden = SESSION.getItem('makaniKbFlagsHidden') === '1';
  const bodyRef = useRef(null);
  const drawerRef = useRef(null);
  const debounceT = useRef(null);
  const [liveQuery, setLiveQuery] = useState('');

  const onQuery = v => {
    setQuery(v);
    clearTimeout(debounceT.current);
    debounceT.current = setTimeout(() => setLiveQuery(v), 140);
  };
  const clearQuery = () => { clearTimeout(debounceT.current); setQuery(''); setLiveQuery(''); };

  /* ---- body ---- */
  const q = liveQuery.trim();
  const rendered = useMemo(() => {
    if (!article) return { title: '', html: '', stems: null, isSearch: false };
    if (q.length >= 2){
      const res = matches(q);
      const stems = searchTerms(q, res.slice(0, 8).map(r => r.a.title + ' . ' + strip(r.a.answer)).join(' . '));
      const title = res.length ? (res[0].coverage < 0.5 ? 'Closest matches' : 'Results') + ' for “' + q + '”' : 'No results for “' + q + '”';
      const html = res.length
        ? '<article>' + res.slice(0, 8).map(r =>
            '<div class="sr" data-go="' + r.a.slug + '"><div class="sr-t">' + r.a.title + '</div>' +
            '<div class="sr-m">' + r.a.module + '</div><div class="sr-x">' + snippetFor(r.a, stems) + '</div></div>').join('') + '</article>'
        : '<article><div class="empty-search"><h3>Nothing found</h3><p>No article covers that yet. The search has been logged so it can be written.</p></div></article>';
      return { title, html, stems, isSearch: true };
    }
    return { title: article.title, html: renderArticle(article, flagsHidden), stems: null, isSearch: false };
  }, [article, q, flagsHidden]);

  /* Search results start at the top; a re-render of the same article does
     not move it. Highlight hits once the HTML is in. */
  const shownKey = useRef('');
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (rendered.stems) highlight(body.querySelector('article'), rendered.stems);
    const key = rendered.isSearch ? 'q:' + q : 'a:' + (article && article.slug);
    if (key !== shownKey.current){ shownKey.current = key; body.scrollTop = 0; }
  }, [rendered, q, article]);

  /* ---- delegated clicks inside the body ---- */
  const onBodyClick = e => {
    const t = e.target;
    if (!t.closest) return;
    const guide = t.closest('[data-guide]');    if (guide){ e.preventDefault(); requestWalkthrough(guide.dataset.guide); return; }
    const goEl = t.closest('[data-go]');
    if (goEl){
      e.preventDefault();
      const target = byId(goEl.dataset.go);
      if (!target) return;                 /* crumb "home" has no article */
      if (target.stub) return;
      setLastArticle(target.slug); announce();
      clearQuery();
      return;
    }
    const modEl = t.closest('[data-mod-go]');
    if (modEl){ e.preventDefault(); navigate(KB_BASE + '/m/' + encodeURIComponent(modEl.dataset.modGo)); return; }
    if (t.closest('[data-help]')){
      const box = bodyRef.current.querySelector('#helpful');
      if (box) box.innerHTML = '<div class="thanks"><span>Thanks — noted.</span></div>';
      return;
    }
    if (t.closest('[data-flagtoggle]')){
      e.preventDefault();
      SESSION.setItem('makaniKbFlagsHidden', flagsHidden ? '0' : '1');
      announce(); setSlug(s => s);        /* re-render */
      return;
    }
    const vh = t.closest('.verify-h');
    if (vh){ e.preventDefault(); const box = vh.closest('.verify'); const c = box.classList.toggle('collapsed'); vh.setAttribute('aria-expanded', c ? 'false' : 'true'); }
  };

  /* ---- resize ---- */
  const startResize = e => {
    e.preventDefault();
    const startX = e.clientX, startW = drawerRef.current.getBoundingClientRect().width;
    document.body.classList.add('dw-resizing');
    const move = ev => {
      const w = Math.min(Math.max(startW + (startX - ev.clientX), DW_MIN), dwMax());
      drawerRef.current.style.width = w + 'px';
    };
    const up = ev => {
      document.body.classList.remove('dw-resizing');
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      patch({ width: Math.min(Math.max(startW + (startX - ev.clientX), DW_MIN), dwMax()) });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  /* ---- render ---- */
  /* Nothing while a walkthrough is running. The drawer exists for the reader
     working from the written steps in another tab; a walkthrough is already
     pointing at the real thing, so the panel is only clutter beside it — and
     it is dimmed and unreadable under the overlay anyway.

     Rendered away rather than dismissed: the drawer's own dismissed/hidden
     state stays exactly as the reader left it, so it comes back the way it
     was with nothing to save and restore. A walkthrough that ends without
     tidying up — a reload halfway through — cannot leave it stuck shut. */
  if (onKbPage || !article || walking) return null;

  const applied = Math.min(Math.max(st.width, DW_MIN), dwMax());
  const collapse = () => patch({ dismissed: true });

  if (st.dismissed){
    if (st.hidden) return null;
    return (
      <div className="kb-handle-wrap on">
        <button className="dw-handle-x" title="Hide help completely" type="button"
          onClick={e => { e.stopPropagation(); patch({ hidden: true }); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <button className="dw-handle" title="Reopen help" type="button" onClick={() => patch({ dismissed: false })}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 0 4 22.5z"/><path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5a2.5 2.5 0 0 1 2.5 2.5z"/></svg>
          Help
        </button>
      </div>
    );
  }

  return (
    <aside className={'kb drawer on' + (flagsHidden ? ' flags-hidden' : '')} ref={drawerRef} style={{ width: applied + 'px' }}>
      <div className="dw-grip" title="Drag to resize" onMouseDown={startResize} />
      <button className="dw-collapse" title="Collapse to the Help tab" aria-label="Collapse help panel" type="button"
        onClick={collapse} onMouseDown={e => e.stopPropagation()}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9 6l6 6-6 6"/></svg>
      </button>

      <div className="dw-head">
        <div className="dw-top">
          <div style={{ minWidth: 0 }}>
            <div className="dw-eyebrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v17H6.5A2.5 2.5 0 0 0 4 22.5z"/><path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v17h4.5a2.5 2.5 0 0 1 2.5 2.5z"/></svg>
              Knowledge Base
            </div>
            <div className="dw-title">{rendered.title}</div>
          </div>
          <div className="dw-acts">
            <button className="dw-btn" title="Open in the Knowledge Base" type="button"
              onClick={() => navigate(KB_BASE + '/a/' + encodeURIComponent(article.slug))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 4h6v6"/><path d="M20 4l-8 8"/><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>
            </button>
            <button className="dw-btn" title="Close" type="button" onClick={collapse}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </div>
        </div>
        <div className="dw-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
          <input type="text" placeholder="Search help…" autoComplete="off" value={query}
            onChange={e => onQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape'){ clearQuery(); e.target.blur(); } }} />
          <button className={'dw-qx' + (query ? ' on' : '')} type="button" onClick={clearQuery}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9.5"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
          </button>
        </div>
        <button className={'dw-back' + (rendered.isSearch ? ' on' : '')} type="button" onClick={clearQuery}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 18l-6-6 6-6"/></svg>Back to the article
        </button>
      </div>

      <div className="dw-body" ref={bodyRef} onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: rendered.html }} />
    </aside>
  );
}
